/**
 * The sentences a parent, a dancer or a teacher reads after tapping Save, in
 * one import-free file so tests/video-save-copy.test.mjs can pin them. A test
 * that only grepped the components let "Saved" come back for a browser
 * download with every gate green (PR #1199 round two, finding 5). These are
 * what the buttons show; the components import them rather than restating
 * them.
 *
 * Feedback UrvXulJH (Amanda, 2026-09-17, "no download"): an R2 video on an
 * iPhone said Saved at the tap while the phone never offered Save Video. The
 * rule since: Saved only when the share sheet took the file.
 */

/** What a finished save says, keyed by lib/saveAttachment.ts's SaveBlobOutcome. */
export const SAVE_OUTCOME_COPY: Readonly<Record<'shared' | 'downloaded' | 'dismissed', string>> = {
  // The share sheet took the file.
  shared: 'Saved',
  // Handed to the browser's download. This app cannot see whether it
  // finished or where it went, so it never says Saved.
  downloaded: 'Download started',
  // The share sheet was closed without picking anything. Nothing to report.
  dismissed: '',
};

/**
 * The line under Download started when an iPhone or iPad could not use the
 * share sheet for an R2 video and fell back to the plain download (see
 * lib/saveVideo.ts). Each says only what is certain: the share sheet was not
 * used, so the video will not go to Photos.
 */
export const SHARE_SHEET_FALLBACK_COPY: Readonly<Record<'too-big' | 'unreachable', string>> = {
  'too-big': 'This video is too big for the share sheet, so it will not go to Photos.',
  unreachable: 'The share sheet could not load this video, so it will not go to Photos.',
};

/**
 * Shown only while fetched bytes are held for one fresh tap, because iOS
 * refused the first share once the tap had expired while the file loaded.
 * It promises the share sheet because only a blob is ever held, and on an
 * iPhone a blob goes to the share sheet first.
 */
export function freshTapPrompt(noun: string): string {
  return `This ${noun} has loaded. Tap again to open the share sheet.`;
}

/** The button's own label while those bytes are held. */
export function freshTapButtonLabel(noun: string): string {
  return `Tap again to save ${noun}`;
}
